import { ApiError, DEFAULT_DATE_FORMAT_FNS } from '@schooly/api';
import { DateSelect } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { DATE_PICKER } from '@schooly/constants/test-ids';
import { format, isAfter } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import {
  pickFormFieldsFromUser,
  UserFormData,
} from '../../../components/common/ProfileModal/profileEditSchema';
import { useProfile } from '../../../context/profile/useProfile';
import IntlError from '../../../utils/intlError';
import { PropertyModal2 } from './PropertyModal2';

import './DateModal.scss';
import { ParentsWarningNotice } from './ParentsWarningNotice';

interface DateModalProps {
  onClose: () => void;
  isOpen: boolean;
}

const DateModal = ({ onClose, isOpen }: DateModalProps) => {
  const { showNotification, showError } = useNotifications();
  const [isUpdating, setIsUpdating] = useState(false);
  const { $t } = useIntl();
  const { user, actions, userType } = useProfile();

  const form = useForm<UserFormData>({
    mode: 'all',
    defaultValues: {
      date_of_birth: user?.date_of_birth,
    },
  });

  const {
    reset,
    formState: { isValid },
  } = form;

  const handleSubmit = useCallback(
    async ({ date_of_birth }: UserFormData) => {
      setIsUpdating(true);

      try {
        const res = await actions.updateProfile({
          date_of_birth: date_of_birth || null,
        });

        if (!res) return;

        showNotification({
          textId: 'confirmation-DateOfBirth',
          type: 'success',
        });
      } catch (e) {
        showError(e as ApiError | IntlError);
      } finally {
        setIsUpdating(false);
        onClose();
      }
    },
    [actions, showNotification, showError, onClose],
  );

  useEffect(() => {
    if (user) {
      reset(pickFormFieldsFromUser(user));
    }
  }, [user, reset]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <PropertyModal2
      isOpen={isOpen}
      user={user}
      isUpdating={isUpdating}
      submitDisabled={!isValid}
      onClose={handleClose}
      onSubmit={handleSubmit}
      form={form}
    >
      <ParentsWarningNotice userType={userType} user={user} />
      <Controller
        control={form.control}
        name="date_of_birth"
        rules={{
          required: true,
          validate: (date_of_birth) => {
            if (!date_of_birth) return;
            if (isAfter(new Date(date_of_birth), Date.now())) {
              return $t({ id: 'error-FutureDateOfBirth' });
            }
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <DateSelect
              ref={field.ref}
              onSetDate={(date) => {
                field.onChange(format(date, DEFAULT_DATE_FORMAT_FNS));
              }}
              date={field.value || ''}
              placeholder={$t({ id: 'peopleDetail-DateOfBirth' })}
              error={fieldState.error}
              requiredLabel="required"
              popperZIndex={(theme) => theme.zIndex.tooltip}
              hideTodayButton
              testId={DATE_PICKER.DATE_SELECT}
            />
          );
        }}
      />
    </PropertyModal2>
  );
};

export default DateModal;
